import { Button, Form, Input, InputNumber, Modal } from 'antd';
import React, { useEffect, useState } from 'react';

import { VENUE_EDIT_FORM_DEFAULT_VALUES } from '../../constants';
import type { VenueEditFormValues } from '../../types';
import { EditOpeningHoursModal } from './components/EditOpeningHoursModal';
import { OperatingHourList } from './components/OperatingHourList';
import { MODAL_BODY_STYLES } from './constants';
import styles from './index.module.scss';

const { Item, useForm } = Form;

type EditModalProps = {
  open: boolean;
  onClose: () => void;
  editingVenue: VenueEditFormValues;
  onSubmit: (formValues: VenueEditFormValues) => void;
};

export const EditModal = ({
  open,
  onClose,
  editingVenue,
  onSubmit,
}: EditModalProps) => {
  const [form] = useForm<VenueEditFormValues>();

  const [showEditOpeningHoursModal, setShowEditOpeningHoursModal] =
    useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('0');
  const [tempOperatingHours, setTempOperatingHours] = useState(
    editingVenue.operatingHours
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue(editingVenue);
      setTempOperatingHours(editingVenue.operatingHours);
    } else {
      setTempOperatingHours(VENUE_EDIT_FORM_DEFAULT_VALUES.operatingHours);
    }
  }, [editingVenue, form, open]);

  const openEditOpeningHoursModal = (tabKey: string) => {
    setCurrentTabKey(tabKey);
    setShowEditOpeningHoursModal(true);
  };

  const closeEditOpeningHoursModal = () => {
    form.setFieldValue('operatingHours', tempOperatingHours);
    setShowEditOpeningHoursModal(false);
  };

  const updateOperatingHours = (
    newOperatingHours: VenueEditFormValues['operatingHours']
  ) => {
    form.setFieldValue('operatingHours', newOperatingHours);
    setTempOperatingHours(newOperatingHours);
    setShowEditOpeningHoursModal(false);
  };

  return (
    <>
      <Modal
        open={open}
        centered
        destroyOnClose
        maskClosable={false}
        onCancel={onClose}
        bodyStyle={MODAL_BODY_STYLES}
        footer={[
          <Button key="cancel" type="primary" onClick={onClose}>
            取消
          </Button>,
          <Button key="ok" onClick={() => form.submit()}>
            確定
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Item
            label="活動空間名稱"
            name="name"
            rules={[{ required: true, message: '必填欄位' }]}
          >
            <Input placeholder="請輸入活動空間名稱" />
          </Item>
          <Item
            label="地址"
            name="address"
            rules={[{ required: true, message: '必填欄位' }]}
          >
            <Input placeholder="請輸入地址" />
          </Item>
          <Item
            label="容納人數"
            name="capacity"
            rules={[
              {
                required: true,
                message: '請輸入有效數值',
                min: 1,
                type: 'number',
              },
            ]}
          >
            <InputNumber
              placeholder="請輸入容納人數"
              min={1}
              className="w-full"
            />
          </Item>
          <Item name="operatingHours" noStyle>
            <Input type="hidden" />
          </Item>
          <Item
            label="開放時間"
            shouldUpdate={(
              prevFormValues: VenueEditFormValues,
              curFormValues: VenueEditFormValues
            ) => {
              return (
                prevFormValues.operatingHours !== curFormValues.operatingHours
              );
            }}
          >
            {({ getFieldValue }) => {
              const operatingHours = getFieldValue('operatingHours');
              return (
                <div className={styles.operatingHoursFieldContainer}>
                  <OperatingHourList
                    operatingHours={operatingHours}
                    openEditOpeningHoursModal={openEditOpeningHoursModal}
                  />

                  <EditOpeningHoursModal
                    open={showEditOpeningHoursModal}
                    closeModal={closeEditOpeningHoursModal}
                    currentTabKey={currentTabKey}
                    setCurrentTabKey={setCurrentTabKey}
                    initialOperatingHours={editingVenue.operatingHours}
                    updateOperatingHours={updateOperatingHours}
                  />
                </div>
              );
            }}
          </Item>
        </Form>
      </Modal>
    </>
  );
};
